import { Controller } from '@hotwired/stimulus'
import { api } from '../../api'
import { TrixEditor } from '../../types/common'
import FocusTrapController from './focus-trap'

interface CategoryChangeEvent extends Event {
  target: HTMLSelectElement
}

interface InitialData {
  accountId?: string
  category?: string
  categoryId?: string
  description?: string
}

export interface NoteData {
  category: string
  categoryId: string
  description: string
  id: string
}

export interface CreatedEvent extends Event {
  detail: {
    note: NoteData
  }
}

export default class FinfolioNoteController extends Controller<HTMLElement> {
  declare accountIdValue: string
  declare categoryValue: string
  declare categoryIdValue: string
  declare descriptionValue: string
  declare editingValue: boolean
  declare idValue: string
  declare initialData: InitialData

  declare readonly cancelButtonTarget: HTMLButtonElement
  declare readonly categoryChipTarget?: HTMLSpanElement
  declare readonly categorySelectTarget: HTMLSelectElement
  declare readonly descriptionEditorTarget: TrixEditor
  declare readonly descriptionPreviewTarget: HTMLDivElement
  declare readonly editButtonTarget?: HTMLButtonElement
  declare readonly focusTrapOutlet: FocusTrapController
  declare readonly saveButtonTarget: HTMLButtonElement

  declare readonly hasCategoryChipTarget: boolean
  declare readonly hasDescriptionPreviewTarget: boolean
  declare readonly hasEditButtonTarget: boolean
  declare readonly hasFocusTrapOutlet: boolean

  static outlets = ['focus-trap']

  static targets = [
    'cancelButton',
    'categoryChip',
    'categorySelect',
    'descriptionEditor',
    'descriptionPreview',
    'editButton',
    'saveButton',
  ]

  static values = {
    accountId: String,
    category: { default: 'Uncategorized', type: String },
    categoryId: String,
    description: String,
    editing: Boolean,
    id: String,
  }

  // Getters

  get modified() {
    return (
      this.categoryValue !== this.initialData.category ||
      this.categoryIdValue !== this.initialData.categoryId ||
      this.descriptionValue !== this.initialData.description
    )
  }

  get noteData() {
    return {
      category: this.categoryValue,
      categoryId: this.categoryIdValue,
      description: this.descriptionValue,
      id: this.idValue,
    } as NoteData
  }

  get valid() {
    return Boolean(this.categoryIdValue && this.descriptionValue)
  }

  // Lifecycle methods

  connect() {
    this.#setInitialData()
    this.element.addEventListener('trix-initialize', this.#hideTrixToolbar.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('trix-initialize', this.#hideTrixToolbar.bind(this))
  }

  // Value change methods

  categoryValueChanged() {
    if (this.hasCategoryChipTarget) {
      this.categoryChipTarget.innerText = this.categoryValue
    }
  }

  categoryIdValueChanged() {
    this.element.classList.toggle('categorized', Boolean(this.categoryIdValue))
  }

  descriptionValueChanged() {
    if (this.hasDescriptionPreviewTarget) {
      this.descriptionPreviewTarget.innerHTML = this.descriptionValue
    }
  }

  editingValueChanged(curr: boolean, prev: boolean) {
    this.element.classList.toggle('editing', this.editingValue)
    this.element.classList.toggle('viewing', !this.editingValue)

    // Stimulus is kinda dumb and fires the value change event on init,
    // but we don't want to focus elements or change the focus trap on init.
    // We only want to do those things after the _user_ has triggered a change.
    if (curr === prev) return

    if (this.editingValue) {
      this.cancelButtonTarget.focus()
    } else if (this.hasEditButtonTarget) {
      this.editButtonTarget.focus()
    }

    if (this.hasFocusTrapOutlet) {
      this.editingValue ? this.focusTrapOutlet.activate() : this.focusTrapOutlet.deactivate()
    }
  }

  // Public methods

  cancel() {
    this.#reset()
    this.#handleAnyChange()
    this.editingValue = false
  }

  create() {
    if (!this.modified) return

    api.admin.notes
      .create({
        categoryId: this.categoryIdValue,
        description: this.descriptionValue,
        entityId: this.accountIdValue,
      })
      .then(response => response.json)
      .then(data => {
        if ('error' in data) throw new Error()

        this.idValue = data.id
        window.app.snackBar.add(data.message)
        this.dispatch('created', { detail: { note: this.noteData } })
        this.#reset()
      })
      .catch(() => alert('The note could not be created'))
  }

  edit() {
    this.editingValue = true
  }

  update() {
    if (!this.modified) return

    api.admin.notes
      .update({
        categoryId: this.categoryIdValue,
        description: this.descriptionValue,
        noteId: this.idValue,
      })
      .then(response => response.json)
      .then(data => {
        if ('error' in data) throw new Error()

        this.#setInitialData()
        this.editingValue = false

        window.app.snackBar.add(data.message)
      })
      .catch(() => alert('The note could not be updated'))
  }

  changeCategory(event: CategoryChangeEvent) {
    const select = event.target
    const selectedOption = select.options[select.selectedIndex]

    this.categoryValue = selectedOption.value ? selectedOption.text : 'Uncategorized'
    this.categoryIdValue = selectedOption.value
    this.#handleAnyChange()
  }

  changeDescription(event: Event) {
    const trixDocument = (event.target as TrixEditor).editor.getDocument()
    this.descriptionValue = trixDocument.toString().replace(/\n$/, '')
    this.#handleAnyChange()
  }

  // Private methods

  #handleAnyChange() {
    this.element.classList.toggle('modified', this.modified)
    this.element.classList.toggle('valid', this.valid)
    this.saveButtonTarget.disabled = !this.valid
  }

  #hideTrixToolbar() {
    this.descriptionEditorTarget.toolbarElement.style.display = 'none'
  }

  #reset() {
    this.accountIdValue = this.initialData.accountId
    this.categoryValue = this.initialData.category
    this.categoryIdValue = this.initialData.categoryId
    this.descriptionValue = this.initialData.description
    this.descriptionEditorTarget.value = this.initialData.description

    for (const option of this.categorySelectTarget.options) {
      if (option.value === this.initialData.categoryId) {
        option.selected = true
        break
      }
    }
  }

  #setInitialData() {
    this.initialData = {
      accountId: this.accountIdValue,
      category: this.categoryValue,
      categoryId: this.categoryIdValue,
      description: this.descriptionValue,
    }
  }
}
